import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Link2, Save, Check, Loader2, ExternalLink, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Integrations() {
  const { token } = useAuth();

  const [microsoftConfig, setMicrosoftConfig] = useState({
    enabled: false,
    client_id: '',
    client_secret: '',
    tenant_id: 'common',
    client_secret_set: false
  });
  const [savingMicrosoft, setSavingMicrosoft] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchIntegrations = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/workspace-settings/microsoft`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMicrosoftConfig(prev => ({ ...prev, ...data }));
      }
    } catch {
      // Microsoft config may not exist yet
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  const handleSaveMicrosoft = async () => {
    setSavingMicrosoft(true);
    try {
      const res = await fetch(`${API_URL}/api/workspace-settings/microsoft`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          enabled: microsoftConfig.enabled,
          client_id: microsoftConfig.client_id,
          client_secret: microsoftConfig.client_secret,
          tenant_id: microsoftConfig.tenant_id
        })
      });
      if (res.ok) {
        toast.success('Microsoft integration settings saved');
        const refreshRes = await fetch(`${API_URL}/api/workspace-settings/microsoft`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (refreshRes.ok) {
          const data = await refreshRes.json();
          setMicrosoftConfig(prev => ({ ...prev, ...data, client_secret: '' }));
        }
      } else {
        toast.error('Failed to save Microsoft settings');
      }
    } catch {
      toast.error('Failed to save Microsoft settings');
    } finally {
      setSavingMicrosoft(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="integrations-page">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Integrations</h1>
          <p className="text-sm text-slate-500 mt-1">Connect your workspace with the tools your team uses every day.</p>
        </div>

        {/* Integration Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* Slack — Coming Soon */}
          <Card className="border-slate-200/60 dark:border-slate-700 relative overflow-hidden" data-testid="integration-slack">
            <div className="absolute inset-0 bg-slate-50/60 dark:bg-slate-800/40 z-10" />
            <CardHeader className="flex flex-row items-start gap-4 pb-2 relative z-20">
              <div className="w-12 h-12 rounded-xl bg-white dark:bg-slate-700 flex items-center justify-center shadow-sm border border-slate-100 dark:border-slate-600 flex-shrink-0">
                <svg className="w-7 h-7" viewBox="0 0 24 24">
                  <path fill="#E01E5A" d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z"/>
                  <path fill="#36C5F0" d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z"/>
                  <path fill="#2EB67D" d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312z"/>
                  <path fill="#ECB22E" d="M15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-sm font-semibold">Slack</CardTitle>
                  <Badge variant="secondary" className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-[10px] font-semibold px-2 py-0" data-testid="slack-coming-soon">
                    <Clock className="w-3 h-3 mr-1" />
                    Coming Soon
                  </Badge>
                </div>
                <p className="text-xs text-slate-500 mt-1">Receive team notifications, approval alerts, and daily summaries in your Slack channels.</p>
              </div>
            </CardHeader>
            <CardContent className="relative z-20">
              <Button variant="outline" size="sm" className="h-8 rounded-lg opacity-50 cursor-not-allowed" disabled data-testid="slack-connect-btn">
                <Link2 className="w-3.5 h-3.5 mr-1.5" />
                Connect
              </Button>
            </CardContent>
          </Card>

          {/* Jira — Coming Soon */}
          <Card className="border-slate-200/60 dark:border-slate-700 relative overflow-hidden" data-testid="integration-jira">
            <div className="absolute inset-0 bg-slate-50/60 dark:bg-slate-800/40 z-10" />
            <CardHeader className="flex flex-row items-start gap-4 pb-2 relative z-20">
              <div className="w-12 h-12 rounded-xl bg-white dark:bg-slate-700 flex items-center justify-center shadow-sm border border-slate-100 dark:border-slate-600 flex-shrink-0">
                <svg className="w-7 h-7" viewBox="0 0 24 24">
                  <path fill="#2684FF" d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005z"/>
                  <path fill="#2684FF" d="M17.447 5.176H5.876a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057a5.215 5.215 0 0 0 5.214 5.214V6.18a1.005 1.005 0 0 0-1.005-1.005z" opacity=".77"/>
                  <path fill="#2684FF" d="M23.308 0H11.737a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057a5.215 5.215 0 0 0 5.214 5.214V1.005A1.005 1.005 0 0 0 23.308 0z" opacity=".55"/>
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-sm font-semibold">Jira</CardTitle>
                  <Badge variant="secondary" className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-[10px] font-semibold px-2 py-0" data-testid="jira-coming-soon">
                    <Clock className="w-3 h-3 mr-1" />
                    Coming Soon
                  </Badge>
                </div>
                <p className="text-xs text-slate-500 mt-1">Import tasks from your Jira board and sync time entries back to issues.</p>
              </div>
            </CardHeader>
            <CardContent className="relative z-20">
              <Button variant="outline" size="sm" className="h-8 rounded-lg opacity-50 cursor-not-allowed" disabled data-testid="jira-connect-btn">
                <Link2 className="w-3.5 h-3.5 mr-1.5" />
                Connect
              </Button>
            </CardContent>
          </Card>

          {/* Google Workspace */}
          <Card className="border-slate-200/60 dark:border-slate-700" data-testid="integration-google-workspace">
            <CardHeader className="flex flex-row items-start gap-4 pb-2">
              <div className="w-12 h-12 rounded-xl bg-white dark:bg-slate-700 flex items-center justify-center shadow-sm border border-slate-100 dark:border-slate-600 flex-shrink-0">
                <svg className="w-7 h-7" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <CardTitle className="text-sm font-semibold">Google Workspace</CardTitle>
                <p className="text-xs text-slate-500 mt-1">Enable SSO login for your team with Google accounts.</p>
              </div>
            </CardHeader>
            <CardContent>
              <Button variant="outline" size="sm" className="h-8 rounded-lg" data-testid="google-workspace-connect-btn">
                <Link2 className="w-3.5 h-3.5 mr-1.5" />
                Connect
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Microsoft 365 — Full Configuration */}
        <Card className="border-slate-200/60 dark:border-slate-700" data-testid="integration-microsoft">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white dark:bg-slate-700 flex items-center justify-center shadow-sm border border-slate-100 dark:border-slate-600 flex-shrink-0">
                  <svg className="w-7 h-7" viewBox="0 0 24 24">
                    <path fill="#F25022" d="M1 1h10v10H1z"/>
                    <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                    <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                    <path fill="#FFB900" d="M13 13h10v10H13z"/>
                  </svg>
                </div>
                <div>
                  <CardTitle className="text-sm font-semibold">Microsoft 365 Calendar</CardTitle>
                  <p className="text-xs text-slate-500 mt-1">Allow employees to sync their Outlook calendar events as time entries.</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={microsoftConfig.enabled}
                  onCheckedChange={(checked) => setMicrosoftConfig(prev => ({ ...prev, enabled: checked }))}
                  data-testid="microsoft-enabled-toggle"
                />
                <span className={`text-xs font-medium ${microsoftConfig.enabled ? 'text-emerald-600' : 'text-slate-500'}`}>
                  {microsoftConfig.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </CardHeader>

          {microsoftConfig.enabled && (
            <CardContent className="space-y-4 pt-0">
              <div className="border-t border-slate-100 dark:border-slate-800 pt-4" />

              {/* Setup Instructions */}
              <div className="bg-blue-50 dark:bg-blue-900/10 p-3 rounded-lg">
                <p className="text-xs text-blue-700 dark:text-blue-400">
                  <strong>Setup:</strong> Create an Azure AD app registration at{' '}
                  <a href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade"
                     target="_blank" rel="noopener noreferrer"
                     className="underline hover:text-blue-800 inline-flex items-center gap-0.5">
                    Azure Portal <ExternalLink className="w-3 h-3" />
                  </a>
                  . Add redirect URI: <code className="bg-blue-100 dark:bg-blue-900/20 px-1 rounded text-[11px]">{window.location.origin}/api/microsoft/callback</code>
                </p>
              </div>

              {/* Config Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600 dark:text-slate-400">Client ID (Application ID)</Label>
                  <Input
                    type="text"
                    value={microsoftConfig.client_id}
                    onChange={(e) => setMicrosoftConfig(prev => ({ ...prev, client_id: e.target.value }))}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    className="h-9 rounded-lg text-sm"
                    data-testid="microsoft-client-id"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600 dark:text-slate-400">Tenant ID</Label>
                  <Input
                    type="text"
                    value={microsoftConfig.tenant_id}
                    onChange={(e) => setMicrosoftConfig(prev => ({ ...prev, tenant_id: e.target.value }))}
                    placeholder="common (or your tenant ID)"
                    className="h-9 rounded-lg text-sm"
                    data-testid="microsoft-tenant-id"
                  />
                  <p className="text-[11px] text-slate-400">Use "common" for multi-tenant or your specific tenant ID</p>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600 dark:text-slate-400">
                  Client Secret {microsoftConfig.client_secret_set && <span className="text-emerald-600">(configured)</span>}
                </Label>
                <Input
                  type="password"
                  value={microsoftConfig.client_secret}
                  onChange={(e) => setMicrosoftConfig(prev => ({ ...prev, client_secret: e.target.value }))}
                  placeholder={microsoftConfig.client_secret_set ? '••••••••••••••••' : 'Enter client secret'}
                  className="h-9 rounded-lg text-sm"
                  data-testid="microsoft-client-secret"
                />
                <p className="text-[11px] text-slate-400">Leave empty to keep existing secret</p>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleSaveMicrosoft}
                  disabled={savingMicrosoft || !microsoftConfig.client_id}
                  className="h-9 rounded-xl"
                  data-testid="save-microsoft-config-btn"
                >
                  {savingMicrosoft ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Save Microsoft Settings</>
                  )}
                </Button>
              </div>
            </CardContent>
          )}
        </Card>
      </div>
    </Layout>
  );
}
